import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, X, Briefcase } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const TRACKS = [
  { id: "data_cloud", label: "Data Cloud" },
  { id: "sfmc", label: "SFMC" },
  { id: "mc_next", label: "MC Next" },
];
const EMPTY_FORM = { title: "", location: "", job_type: "Full-time", description: "", track: "data_cloud", salary_range: "", apply_url: "", experience_level: "Mid" };
const EXPERIENCE_LEVELS = ["Entry", "Mid", "Senior", "Lead"];

export default function JobPostingsPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/recruiter/jobs").then((r) => setJobs(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); };
  const openEdit = (job) => { setForm({ title: job.title, location: job.location, job_type: job.job_type, description: job.description, track: job.track, salary_range: job.salary_range || "", apply_url: job.apply_url || "", experience_level: job.experience_level || "Mid" }); setEditingId(job.id); setShowForm(true); };

  const save = async () => {
    if (!form.title || !form.location || !form.description) {
      toast.error("Title, location and description are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/recruiter/jobs/${editingId}`, form);
        toast.success("Job posting updated");
      } else {
        await api.post("/recruiter/jobs", form);
        toast.success("Job posted — now live on the Job Board");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save job posting");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (jobId) => {
    try {
      await api.delete(`/recruiter/jobs/${jobId}`);
      toast.success("Job posting deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const toggleActive = async (job) => {
    try {
      await api.put(`/recruiter/jobs/${job.id}`, { is_active: !job.is_active });
      load();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div data-testid="recruiter-job-postings-panel">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-400">Manage your company's job listings — active postings appear on the public Job Board.</p>
        <button onClick={openCreate} data-testid="recruiter-create-job-btn" className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-3.5 py-2 rounded-lg shrink-0">
          <Plus className="w-3.5 h-3.5" /> Post a Job
        </button>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto my-10" />
      ) : (
        <div className="space-y-3" data-testid="recruiter-job-list">
          {jobs.map((job) => (
            <div key={job.id} className="neu-card rounded-xl p-4 flex items-start justify-between gap-4" data-testid={`recruiter-job-item-${job.id}`}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-white">{job.title}</p>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${job.is_active ? "bg-emerald-400/15 text-emerald-300" : "bg-slate-500/15 text-slate-400"}`}>
                    {job.is_active ? "Active" : "Closed"}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{job.location} · {job.job_type} · {job.track.replace("_", " ")}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => toggleActive(job)} data-testid={`recruiter-job-toggle-${job.id}`} className="text-xs font-semibold text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 px-2.5 py-1.5 rounded-lg">
                  {job.is_active ? "Close" : "Reopen"}
                </button>
                <button onClick={() => openEdit(job)} data-testid={`recruiter-job-edit-${job.id}`} className="text-slate-400 hover:text-indigo-300 p-1.5"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(job.id)} data-testid={`recruiter-job-delete-${job.id}`} className="text-slate-400 hover:text-rose-400 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          {jobs.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-10 flex flex-col items-center gap-2" data-testid="recruiter-no-jobs">
              <Briefcase className="w-6 h-6 text-slate-400" /> No job postings yet. Post your first one!
            </p>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="recruiter-job-form-modal">
          <div className="glass-panel rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-white text-sm">{editingId ? "Edit Job Posting" : "Post a New Job"}</h3>
              <button onClick={() => setShowForm(false)} data-testid="recruiter-job-form-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Job Title *</label>
                <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="recruiter-job-title-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Location *</label>
                  <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} data-testid="recruiter-job-location-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Job Type</label>
                  <input value={form.job_type} onChange={(e) => setForm((f) => ({ ...f, job_type: e.target.value }))} data-testid="recruiter-job-type-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Track</label>
                <select value={form.track} onChange={(e) => setForm((f) => ({ ...f, track: e.target.value }))} data-testid="recruiter-job-track-select" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400">
                  {TRACKS.map((t) => <option key={t.id} value={t.id} className="bg-[#0f1522]">{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Experience Level</label>
                <select value={form.experience_level} onChange={(e) => setForm((f) => ({ ...f, experience_level: e.target.value }))} data-testid="recruiter-job-experience-select" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400">
                  {EXPERIENCE_LEVELS.map((l) => <option key={l} value={l} className="bg-[#0f1522]">{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Description *</label>
                <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={4} data-testid="recruiter-job-description-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Salary Range</label>
                  <input value={form.salary_range} onChange={(e) => setForm((f) => ({ ...f, salary_range: e.target.value }))} placeholder="e.g. $80k-$110k" data-testid="recruiter-job-salary-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">External Apply URL</label>
                  <input value={form.apply_url} onChange={(e) => setForm((f) => ({ ...f, apply_url: e.target.value }))} placeholder="Optional" data-testid="recruiter-job-apply-url-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                </div>
              </div>
            </div>
            <button onClick={save} disabled={saving} data-testid="recruiter-job-save-btn" className="w-full mt-5 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {editingId ? "Save Changes" : "Post Job"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
